import React, { useCallback, useEffect, useState } from 'react';
import { Search, UserPlus, Play, Trash2, RefreshCw, ClipboardList, AlertTriangle, CheckCircle2, Archive, ArchiveRestore } from 'lucide-react';
import api from '../../lib/api';
import { toast } from '../../hooks/use-toast';
import { formatCurrency } from '../../mock';

const STATUS_CLS = {
  Waiting: 't-warn', Assigned: 'text-primary', 'In Progress': 'text-primary',
  Completed: 't-ok', 'Needs Recon': 't-bad', 'Final QC': 't-warn', Ready: 't-ok',
};
const QUICK = [['', 'Todas'], ['Waiting', 'Waiting'], ['mine', 'Minhas inspeções'], ['In Progress', 'In Progress'],
  ['Needs Recon', 'Needs Recon'], ['Final QC', 'Final QC'], ['Completed', 'Completed'], ['Ready', 'Ready'], ['archived', 'Arquivadas']];
const PRIORITIES = ['Critical', 'High', 'Medium', 'Low'];

export const fmtWait = (min) => (min >= 60 ? `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, '0')}m` : `${min}m`);

export const InspectionQueue = ({ onOpen, canEdit, userId }) => {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [team, setTeam] = useState([]);
  const [quick, setQuick] = useState('');
  const [priority, setPriority] = useState('');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    const params = { q };
    if (quick === 'mine') params.mine = '1';
    else if (quick === 'archived') params.archived = '1';
    else if (quick) params.status = quick;
    if (priority) params.priority = priority;
    try {
      const [{ data: r }, { data: s }] = await Promise.all([api.get('/inspections', { params }), api.get('/inspections/stats')]);
      setRows(r); setStats(s);
    } catch (e) {}
  }, [quick, priority, q]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/service/inspection-team').then(({ data }) => setTeam(data)).catch(() => {}); }, []);

  const archive = async (i, undo = false) => {
    if (!undo && !window.confirm(`Arquivar a inspeção de ${i.vehicle_desc}? Ela sai da fila; continua acessível pela busca e no card do veículo em Estoque.`)) return;
    setBusy(i.id);
    try { await api.post(`/inspections/${i.id}/${undo ? 'unarchive' : 'archive'}`); toast({ title: undo ? 'Inspeção de volta à fila' : 'Inspeção arquivada' }); await load(); }
    catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
    finally { setBusy(''); }
  };

  const assign = async (i, uid) => {
    setBusy(i.id);
    try { await api.put(`/inspections/${i.id}/assign`, { user_id: uid }); await load(); }
    catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
    finally { setBusy(''); }
  };

  const start = async (i) => {
    setBusy(i.id);
    try { await api.post(`/inspections/${i.id}/start`); onOpen(i.id); }
    catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
    finally { setBusy(''); }
  };

  const del = async (i) => {
    if (!window.confirm(`Excluir inspeção?\n\n${i.vehicle_desc} · Stock #${i.stock}\n\nOs resultados da inspeção serão removidos. O veículo NÃO será excluído.`)) return;
    try {
      await api.delete(`/inspections/${i.id}`);
      toast({ title: 'Inspeção excluída' });
      await load();
    } catch (e) {
      if (e.response?.status === 409) {
        if (window.confirm(`${e.response.data.detail}\n\nExcluir a inspeção E os itens de Recon (labor, peças e custos)?`)) {
          await api.delete(`/inspections/${i.id}`, { params: { force: '1' } });
          toast({ title: 'Inspeção e Recon excluídos' });
          await load();
        }
      } else toast({ title: 'Erro', description: e.response?.data?.detail || '' });
    }
  };

  const backfill = async () => {
    try {
      const { data } = await api.post('/inspections/backfill');
      toast({ title: data.created ? `${data.created} inspeção(ões) criada(s)` : 'Todo o estoque já tem inspeção' });
      await load();
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  return (
    <div className="space-y-3" data-testid="inspection-queue">
      <div className="ent-ribbon" data-testid="inspection-stats">
        {[['Waiting', stats.waiting, 't-warn'], ['Assigned', stats.assigned, ''], ['In Progress', stats.in_progress, ''],
          ['Needs Recon', stats.needs_recon, 't-bad'], ['Final QC', stats.final_qc, 't-warn'],
          ['Completed hoje', stats.completed_today, 't-ok'], ['Recon aberto', stats.recon_open, ''],
          ['Custo Recon', formatCurrency(stats.recon_total_cost || 0), '']].map(([l, v, cls]) => (
          <div key={l}><p className="ent-label whitespace-nowrap">{l}</p><p className={`ent-value mt-1 ${cls}`} data-testid={`insp-stat-${l.replace(/\s/g, '-').toLowerCase()}`}>{v ?? 0}</p></div>
        ))}
        <div>
          <p className="ent-label whitespace-nowrap">Mais antiga aguardando</p>
          <p className={`ent-value mt-1 ${(stats.oldest_waiting_min || 0) > 240 ? 't-bad' : ''}`} data-testid="insp-oldest">{fmtWait(stats.oldest_waiting_min || 0)}</p>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {/* no celular os filtros viram carrossel; no desktop md:contents devolve a linha original */}
        <div className="mob-chiprow flex items-center gap-2 md:contents" data-testid="insp-quick-filters">
          {QUICK.map(([k, label]) => (
            <button key={k || 'all'} data-testid={`insp-filter-${k ? k.replace(/\s/g, '-').toLowerCase() : 'all'}`} onClick={() => setQuick(k)}
              className={`ent-btn ${quick === k ? 'ent-btn-primary' : ''}`}>{label}</button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[150px] md:flex-none">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/60 pointer-events-none" />
          <input data-testid="insp-search" className="ent-input pl-8 w-full md:w-[210px]" placeholder="Veículo, stock, VIN..." value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <select data-testid="insp-priority-filter" className="ent-input w-full md:w-auto" value={priority} onChange={(e) => setPriority(e.target.value)}>
          <option value="" className="bg-popover">Todas as prioridades</option>
          {PRIORITIES.map((p) => <option key={p} className="bg-popover">{p}</option>)}
        </select>
        {canEdit && <button data-testid="insp-backfill" onClick={backfill} className="ent-btn w-full md:w-auto md:ml-auto"><RefreshCw className="w-3.5 h-3.5" /> Gerar inspeções do estoque</button>}
      </div>

      <div className="ent-panel overflow-hidden">
        <div className="ent-colhead" style={{ borderTop: '2px solid #3b82f6' }}>
          <ClipboardList className="w-3.5 h-3.5 text-primary" />
          <span className="ent-coltitle uppercase tracking-wide">Inspection Queue</span>
          <span className="ent-pill" data-testid="insp-count">{rows.length}</span>
        </div>
        {rows.length > 0 && (
          <div className="md:hidden divide-y divide-border/60" data-testid="insp-mobile-list">
            {rows.map((i) => (
              <div key={i.id} data-testid={`insp-card-${i.id}`} onClick={() => onOpen(i.id)} className="p-3 space-y-2 active:bg-primary/5">
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold leading-tight truncate">{i.vehicle_desc}</p>
                    <p className="mono text-[10.5px] t-label truncate">#{i.stock} · {i.vin || '—'}</p>
                  </div>
                  <span className={`ent-status shrink-0 ${STATUS_CLS[i.status] || ''}`}><span className="ent-dot" />{i.status}</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap text-[11px]">
                  <span className={`ent-tag ${i.priority === 'Critical' ? 't-bad' : i.priority === 'High' ? 't-warn' : ''}`}>{i.priority}</span>
                  <span className={`num ${i.waiting_minutes > 240 && i.status === 'Waiting' ? 't-bad' : 't-label'}`}>{fmtWait(i.waiting_minutes || 0)} de espera</span>
                  <span className="t-label truncate">{i.assigned_to_name || 'Sem responsável'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-1.5 flex-1 bg-muted/60 rounded-sm overflow-hidden"><div className="h-full bg-primary" style={{ width: `${i.progress?.pct || 0}%` }} /></div>
                  <span className="num text-[11px] t-label shrink-0">{i.progress?.checked || 0}/{i.progress?.total || 0}</span>
                  {i.recon?.total ? (
                    <span className={`ent-status shrink-0 ${i.recon.open ? 't-bad' : 't-ok'}`}>
                      {i.recon.open ? `${i.recon.open} recon` : 'Recon OK'}
                    </span>
                  ) : null}
                </div>
                <div className="flex items-center gap-1.5 pt-0.5" onClick={(e) => e.stopPropagation()}>
                  {canEdit && ['Waiting', 'Assigned'].includes(i.status) ? (
                    <button data-testid={`insp-mstart-${i.id}`} onClick={() => start(i)} className="ent-btn ent-btn-sm text-primary border-primary/50 flex-1"><Play className="w-3 h-3" /> Start</button>
                  ) : (
                    <button data-testid={`insp-mopen-${i.id}`} onClick={() => onOpen(i.id)} className="ent-btn ent-btn-sm flex-1">Abrir</button>
                  )}
                  {canEdit && i.status === 'Waiting' && !i.assigned_to && userId && (
                    <button data-testid={`insp-mself-${i.id}`} title="Atribuir a mim" onClick={() => assign(i, userId)} className="ent-btn ent-btn-icon border-0"><UserPlus className="w-4 h-4" /></button>
                  )}
                  {canEdit && i.status === 'Ready' && !i.archived && <button data-testid={`insp-marchive-${i.id}`} title="Arquivar" disabled={busy === i.id} onClick={() => archive(i)} className="ent-btn ent-btn-icon border-0"><Archive className="w-4 h-4" /></button>}
                  {canEdit && i.archived && <button data-testid={`insp-munarchive-${i.id}`} title="Desarquivar" onClick={() => archive(i, true)} className="ent-btn ent-btn-icon border-0"><ArchiveRestore className="w-4 h-4" /></button>}
                  {canEdit && <button data-testid={`insp-mdelete-${i.id}`} title="Excluir inspeção" onClick={() => del(i)} className="ent-btn ent-btn-icon border-0 hover:text-[hsl(var(--bad))]"><Trash2 className="w-4 h-4" /></button>}
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="overflow-x-auto hidden md:block">
          {rows.length === 0 ? (
            <p className="ent-empty">Nenhuma inspeção neste filtro</p>
          ) : (
            <table className="ent-table">
              <thead>
                <tr>
                  <th>Veículo</th>
                  <th className="hidden lg:table-cell">Stock #</th>
                  <th className="text-right hidden xl:table-cell">Mileage</th>
                  <th className="hidden xl:table-cell">Entrada</th>
                  <th>Espera</th>
                  <th className="hidden lg:table-cell">Prioridade</th>
                  <th>Responsável</th>
                  <th>Status</th>
                  <th>Progresso</th>
                  <th>Recon</th>
                  <th className="text-right">Ação</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((i) => (
                  <tr key={i.id} data-testid={`insp-row-${i.id}`} className="cursor-pointer" onClick={() => onOpen(i.id)}>
                    <td>
                      <p className="font-medium">{i.vehicle_desc}</p>
                      <p className="mono text-[11px] t-label">{i.vin || '—'}</p>
                    </td>
                    <td className="mono hidden lg:table-cell">#{i.stock}</td>
                    <td className="num text-right hidden xl:table-cell">{(i.mileage || 0).toLocaleString('en-US')} mi</td>
                    <td className="num hidden xl:table-cell">{(i.created_at || '').slice(0, 10).split('-').reverse().join('/')}</td>
                    <td className={`num font-semibold ${i.waiting_minutes > 240 && i.status === 'Waiting' ? 't-bad' : ''}`}>{fmtWait(i.waiting_minutes || 0)}</td>
                    <td className="hidden lg:table-cell">
                      <span className={`ent-tag ${i.priority === 'Critical' ? 't-bad' : i.priority === 'High' ? 't-warn' : ''}`}>{i.priority}</span>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {canEdit ? (
                        <select data-testid={`insp-assign-${i.id}`} disabled={busy === i.id} className="ent-input" style={{ width: 140 }} value={i.assigned_to || ''} onChange={(e) => assign(i, e.target.value)}>
                          <option value="" className="bg-popover">— Não atribuída</option>
                          {team.map((t) => <option key={t.user_id} value={t.user_id} className="bg-popover">{t.name}</option>)}
                        </select>
                      ) : (i.assigned_to_name || <span className="t-label">—</span>)}
                    </td>
                    <td><span className={`ent-status ${STATUS_CLS[i.status] || ''}`} data-testid={`insp-status-${i.id}`}><span className="ent-dot" />{i.status}</span>{i.archived && <span className="ent-tag ml-1" data-testid={`insp-archived-${i.id}`}>Arquivada</span>}</td>
                    <td>
                      <div className="flex items-center gap-1.5">
                        <div className="h-1.5 w-14 bg-muted/60 rounded-sm overflow-hidden"><div className="h-full bg-primary" style={{ width: `${i.progress?.pct || 0}%` }} /></div>
                        <span className="num text-[11px] t-label">{i.progress?.checked || 0}/{i.progress?.total || 0}</span>
                      </div>
                    </td>
                    <td>
                      {i.recon?.total ? (
                        <span className={`ent-status ${i.recon.open ? 't-bad' : 't-ok'}`} data-testid={`insp-recon-${i.id}`}>
                          {i.recon.open ? <AlertTriangle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
                          {i.recon.open ? `${i.recon.open} aberto(s)` : 'OK'} · {formatCurrency(i.recon.cost)}
                        </span>
                      ) : <span className="t-label">—</span>}
                    </td>
                    <td className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {canEdit && ['Waiting', 'Assigned'].includes(i.status) && (
                          <button data-testid={`insp-start-${i.id}`} onClick={() => start(i)} className="ent-btn ent-btn-sm text-primary border-primary/50"><Play className="w-3 h-3" /> Start</button>
                        )}
                        {(!canEdit || !['Waiting', 'Assigned'].includes(i.status)) && (
                          <button data-testid={`insp-open-${i.id}`} onClick={() => onOpen(i.id)} className="ent-btn ent-btn-sm">Abrir</button>
                        )}
                        {canEdit && i.status === 'Waiting' && !i.assigned_to && userId && (
                          <button data-testid={`insp-self-${i.id}`} title="Atribuir a mim" onClick={() => assign(i, userId)} className="ent-btn ent-btn-icon border-0"><UserPlus className="w-3.5 h-3.5" /></button>
                        )}
                        {canEdit && i.status === 'Ready' && !i.archived && (
                          <button data-testid={`insp-archive-${i.id}`} disabled={busy === i.id} onClick={() => archive(i)} className="ent-btn ent-btn-sm"><Archive className="w-3 h-3" /> Arquivar</button>
                        )}
                        {canEdit && i.archived && (
                          <button data-testid={`insp-unarchive-${i.id}`} onClick={() => archive(i, true)} className="ent-btn ent-btn-sm"><ArchiveRestore className="w-3 h-3" /> Desarquivar</button>
                        )}
                        {canEdit && <button data-testid={`insp-delete-${i.id}`} title="Excluir inspeção" onClick={() => del(i)} className="ent-btn ent-btn-icon border-0 hover:text-[hsl(var(--bad))]"><Trash2 className="w-3.5 h-3.5" /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {rows.length === 0 && <p className="ent-empty md:hidden">Nenhuma inspeção neste filtro</p>}
      </div>
    </div>
  );
};

export default InspectionQueue;
